<script lang="ts">
  import type { DisplayView, PublicPlayer } from '@banger/shared';
  import { standingsTitle } from '../lib/trackCopy';
  import Avatar from '../shared/Avatar.svelte';
  import PatternGrid from '../shared/PatternGrid.svelte';

  let { view, byId, steals }: { view: DisplayView; byId: Map<string, PublicPlayer>; steals: string[] } = $props();

  const pattern = $derived(view.track === 2 ? view.pattern : null);
  /** Squares in the goal: a line (5), the Remix pattern, or the whole card (Track 3). */
  const goal = $derived(view.track === 3 ? 25 : (pattern?.cells.length ?? 5));
  /** Dots read well up to a pattern's 9 squares; a full card shows a number instead. */
  const dots = $derived(goal <= 9);
  const album = $derived(view.mode === 'album');
  const top = $derived(
    view.players
      .filter((p): p is PublicPlayer & { squaresToGoal: number } => p.squaresToGoal !== null)
      .sort((a, b) => a.squaresToGoal - b.squaresToGoal || (b.points ?? 0) - (a.points ?? 0))
      .slice(0, 3),
  );
  const tiedNames = $derived((view.tiebreak ?? []).map((id) => byId.get(id)?.name ?? '?').join(' vs '));
</script>

<main>
  {#if view.tiebreak}
    <h1 class="tb neon">TIEBREAK! {tiedNames} — first correct answer wins</h1>
  {:else}
    <div class="title">
      {#if pattern}<PatternGrid cells={pattern.cells} label={pattern.label} size="clamp(5.5rem, 13vh, 9rem)" />{/if}
      <h1 class="head" class:platinum={view.track === 3 && album}>{standingsTitle(view)}</h1>
    </div>
  {/if}

  <ol class="rows" class:album>
    {#each top as p, i (p.id)}
      <li style="--d:{i * 0.06}s">
        <Avatar emoji={p.emoji} size={88} />
        <span class="name">{p.name}</span>
        {#if dots}
          <span class="dots" aria-label="{goal - p.squaresToGoal} of {goal}">
            {#each Array(goal) as _, k}<span class:on={k < goal - p.squaresToGoal}>{k < goal - p.squaresToGoal ? '●' : '○'}</span>{/each}
          </span>
        {:else}
          <span class="left"><b>{p.squaresToGoal}</b> {p.squaresToGoal === 1 ? 'square' : 'squares'} left</span>
        {/if}
        <span class="badge">{#if p.squaresToGoal === 1}<span class="away neon">1 away!</span>{/if}</span>
        {#if album}<span class="pts"><b>{p.points ?? 0}</b> pts</span>{/if}
      </li>
    {/each}
  </ol>

  {#each steals as line}<p class="steal">{line}</p>{/each}

  {#if view.reveal}
    <p class="was">♫ {view.reveal.title} · {view.reveal.artist} · {view.reveal.year}</p>
  {/if}
</main>

<style>
  main { min-height: 100%; display: grid; align-content: center; justify-items: center; gap: 3.5vh; padding: 4vh 4vw; text-align: center; }
  .title { display: flex; align-items: center; justify-content: center; gap: 2.5vw; flex-wrap: wrap; }
  .head { margin: 0; font-size: clamp(2.4rem, 5vw, 4.6rem); font-weight: 900; color: var(--cyan); text-shadow: 0 0 14px rgba(125, 249, 255, 0.7); }
  .tb { margin: 0; font-size: clamp(2.2rem, 4.6vw, 4.4rem); font-weight: 900; max-width: 22ch; line-height: 1.1; animation: throb 1.2s ease-in-out infinite; }
  .rows { list-style: none; margin: 0; padding: 0; display: grid; gap: 2.4vh; }
  /* The big font lives on the li so the ch-based name column is measured at the name's size. */
  li {
    display: grid; grid-template-columns: auto 12ch auto 6ch; align-items: center; gap: 2vw; text-align: left;
    font-size: clamp(2rem, 4vw, 3.6rem); animation: slide 0.35s var(--d) ease-out both;
  }
  .album li { grid-template-columns: auto 10ch auto 5ch 4.5ch; font-size: clamp(1.7rem, 3.4vw, 3.2rem); }
  .head.platinum { color: #fff; text-shadow: 0 0 10px #fff, 0 0 26px #b8c4d6, 0 0 54px #7df9ff; }
  .left { font-weight: 700; white-space: nowrap; }
  .left b { color: var(--pink); text-shadow: 0 0 10px var(--pink); font-variant-numeric: tabular-nums; }
  .pts { font-weight: 700; white-space: nowrap; font-size: 0.8em; color: var(--cyan); justify-self: end; }
  .pts b { font-size: 1.15em; font-variant-numeric: tabular-nums; text-shadow: 0 0 10px rgba(125, 249, 255, 0.7); }
  .name { font-weight: 900; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .dots { letter-spacing: 0.12em; color: rgba(255, 255, 255, 0.35); }
  .dots .on { color: var(--pink); text-shadow: 0 0 10px var(--pink); }
  .away { font-size: 0.85em; white-space: nowrap; font-weight: 900; animation: throb 0.9s ease-in-out infinite; }
  .steal { margin: 0; font-size: clamp(1.6rem, 3vw, 2.8rem); font-weight: 800; animation: slide 0.35s 0.3s ease-out both; }
  .was { margin: 0; font-size: clamp(1rem, 1.8vw, 1.5rem); opacity: 0.8; }
  @keyframes slide { from { transform: translateX(-60px); opacity: 0; } to { transform: none; opacity: 1; } }
  @keyframes throb { 50% { transform: scale(1.06); } }
</style>
